import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CuiIconComponent } from '@idealink-material/ui-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { AUTH_SERVICE_TOKEN } from '@/core/util/auth.token';
import { IAuthSession } from '@/core/interface/auth-service.interface';
import { ProfileDialogService } from '@/share/services/profile-dialog.service';
import { AppI18nService } from '@/core/service/app-i18n-service';
import { Language } from '@/core/util/languages.constant';
import { CuiDividerComponent } from '@idealink-material/ui-core';
import { LayoutService } from '@/layout/service/layout.service';

interface UserMenuAction {
  id: string;
  label: string;
  icon: string;
  command: () => void;
}

/**
 * Slim top header bar: menu-collapse toggle on the left, language/user menu
 * on the right. Logo + feature nav live in `app-sidebar`.
 */
@Component({
  selector: 'app-topnav',
  standalone: true,
  imports: [CuiIconComponent, TranslatePipe, CuiDividerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav class="relative flex items-center justify-between h-16 px-6 bg-white border-b border-[var(--border-default)]">
      <button
        type="button"
        class="grid place-items-center w-9 h-9 rounded-lg text-[#475467] hover:bg-black/5 transition-colors cursor-pointer"
        (click)="layoutService.onMenuToggle()"
      >
        <cui-icon name="menu" size="sm" color="#475467" />
      </button>

      <div class="flex items-center gap-4">
        <div class="relative">
          <button
            type="button"
            class="grid place-items-center w-8 h-8 rounded-full overflow-hidden ring-1 ring-black/10 cursor-pointer"
            (click)="toggleLanguageMenu($event)"
          >
            <span
              class="block w-full h-full [&_svg]:w-full [&_svg]:h-full [&_svg]:block"
              [innerHTML]="currentFlag"
            ></span>
          </button>

          @if (languageMenuOpen()) {
            <div
              class="absolute right-0 top-full mt-2 min-w-[200px] rounded-xl bg-white shadow-lg py-2 z-20"
            >
              @for (lang of i18n.languages(); track lang.value) {
                <button
                  type="button"
                  class="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-black/5 transition-colors cursor-pointer"
                  [class.font-bold]="lang.value === i18n.currentLang()"
                  [class.text-[#1f2933]]="lang.value === i18n.currentLang()"
                  [class.text-gray-600]="lang.value !== i18n.currentLang()"
                  (click)="selectLanguage(lang, $event)"
                >
                  <span
                    class="w-5 h-5 flex-shrink-0 [&_svg]:w-full [&_svg]:h-full"
                    [innerHTML]="sanitize(lang.flagSvg)"
                  ></span>
                  <span class="flex-1 text-left">{{ 'topbar.' + lang.value | translate }}</span>
                  @if (lang.value === i18n.currentLang()) {
                    <cui-icon name="check" size="xs" />
                  }
                </button>
              }
            </div>
          }
        </div>
        <p-divider orientation="vertical"></p-divider>
        <div class="relative">
          <button
            type="button"
            class="flex items-center gap-2 cursor-pointer"
            (click)="toggleUserMenu($event)"
          >
            <img [src]="profile.avatarUrl" class="w-8 h-8 rounded-full object-cover" />
            <span class="text-sm font-medium text-[#172033]">{{ profile.name }}</span>
            <cui-icon name="expand_more" size="xs" color="#475467" />
          </button>

          @if (userMenuOpen()) {
            <div
              class="absolute right-0 top-full mt-2 min-w-[220px] rounded-xl bg-white shadow-lg py-2 z-20 "
            >
              <button
                type="button"
                class="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-black/5 transition-colors cursor-pointer"
                (click)="openProfile($event)"
              >
                <img
                  [src]="profile.avatarUrl"
                  class="w-9 h-9 rounded-full object-cover flex-shrink-0"
                />
                <span class="flex flex-col min-w-0">
                  <span class="text-sm font-medium text-[#1f2933] truncate">{{
                    profile.name
                  }}</span>
                  <span class="text-xs text-gray-400 truncate"
                    >{{ 'topbar.lastLogin' | translate }}: {{ profile.lastLogin }}</span
                  >
                </span>
              </button>

            <div class="h-px bg-black/5 my-2 mx-2"></div>

            @for (action of userMenuActions; track action.id) {
              <button
                type="button"
                class="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-black/5 transition-colors cursor-pointer"
                (click)="invokeUserAction(action, $event)"
              >
                <cui-icon [name]="action.icon" size="xs" />
                {{ action.label | translate }}
              </button>
            }
          </div>
        }
        </div>
      </div>
    </nav>
  `,
})
export class AppTopnav {
  private readonly authService: IAuthSession = inject(AUTH_SERVICE_TOKEN);
  private readonly profileDialogService = inject(ProfileDialogService);
  private readonly sanitizer = inject(DomSanitizer);
  readonly i18n = inject(AppI18nService);
  readonly layoutService = inject(LayoutService);

  readonly profile = {
    name: 'Albert Iris',
    avatarUrl: 'https://i.pravatar.cc/120?img=68',
    lastLogin: '03 July 2026 1:40pm',
  };

  readonly userMenuActions: UserMenuAction[] = [
    {
      id: 'logout',
      label: 'topbar.logout',
      icon: 'logout',
      command: () => this.authService.logout(),
    },
  ];

  readonly userMenuOpen = signal(false);
  readonly languageMenuOpen = signal(false);

  toggleUserMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.languageMenuOpen.set(false);
    this.userMenuOpen.update((open) => !open);
  }

  toggleLanguageMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.userMenuOpen.set(false);
    this.languageMenuOpen.update((open) => !open);
  }

  selectLanguage(lang: Language, event: MouseEvent): void {
    event.stopPropagation();
    this.languageMenuOpen.set(false);
    this.i18n.switchLanguage(lang.value);
  }

  sanitize(svg: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  }

  get currentFlag(): SafeHtml {
    const lang = this.i18n.languages().find((l) => l.value === this.i18n.currentLang());
    return this.sanitize(lang?.flagSvg ?? '');
  }

  openProfile(event: MouseEvent): void {
    event.stopPropagation();
    this.userMenuOpen.set(false);
    this.profileDialogService.open();
  }

  invokeUserAction(action: UserMenuAction, event: MouseEvent): void {
    event.stopPropagation();
    this.userMenuOpen.set(false);
    action.command();
  }

  @HostListener('document:click')
  closeMenus(): void {
    this.userMenuOpen.set(false);
    this.languageMenuOpen.set(false);
  }
}
